import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, TypographyStyledGlobal } from '../../../../theme';
import QuestionListPanel from '../../../QuestionListPanel/QuestionListPanel';
import SelectedQuestionsPanel from '../../../SelectedQuestionsPanel/SelectedQuestionsPanel';
import SimpleActionButton from '../../ActionButton/simpleActionButton';
import { SelectedQuestion, SelectedQuestionSettings } from '../addSurveyInterface';
import {
    StepConfigureActionsStyled,
    StepConfigureContainerStyled,
    StepConfigureFooterStyled,
    StepConfigurePanelStyled,
} from './styledStepConfigure';

interface StepSelectFiltersProps {
    selectedQuestions: SelectedQuestion[];
    selectedFilters: SelectedQuestion[];
    onSelectedFiltersChange: (filters: SelectedQuestion[]) => void;
    handleNextStep: (formId: string) => void
}

const StepSelectFilters: FC<StepSelectFiltersProps> = ({
    selectedQuestions,
    selectedFilters,
    onSelectedFiltersChange,
    handleNextStep,
}) => {
    const { t } = useTranslation('settings');

    const [pendingSelectedIds, setPendingSelectedIds] = useState<number[]>([]);

    const selectedFilterIds = selectedFilters.map((sq) => sq.question.id);

    const availableQuestions = selectedQuestions
        .filter((sq) => ['Single', 'Multiple'].includes(sq.question.details.type))
        .filter((sq) => !selectedFilterIds.includes(sq.question.id))
        .map((sq) => sq.question);

    const handleAddSelected = () => {
        const questionsToAdd = selectedQuestions.filter((sq) => pendingSelectedIds.includes(sq.question.id));
        onSelectedFiltersChange([...selectedFilters, ...questionsToAdd]);
        setPendingSelectedIds([]);
    };

    const handleRename = useCallback(
        (questionId: number, name: string) => {
            onSelectedFiltersChange(
                selectedFilters.map((sq) => (sq.question.id === questionId ? { ...sq, customName: name } : sq)),
            );
        },
        [selectedFilters, onSelectedFiltersChange],
    );

    const handleRemove = useCallback(
        (questionId: number) => {
            onSelectedFiltersChange(selectedFilters.filter((sq) => sq.question.id !== questionId));
        },
        [selectedFilters, onSelectedFiltersChange],
    );

    const handleConfigure = useCallback(
        (questionId: number, customName: string, settings: SelectedQuestionSettings) => {
            onSelectedFiltersChange(
                selectedFilters.map((sq) => {
                    if (sq.question.id === questionId) { return { ...sq, customName, settings }; }

                    return sq;
                }),
            );
        },
        [selectedFilters, onSelectedFiltersChange],
    );

    return (
        <>
            <StepConfigureContainerStyled>
                <StepConfigurePanelStyled>
                    <QuestionListPanel
                        onSelectionChange={setPendingSelectedIds}
                        questions={availableQuestions}
                        selectedIds={pendingSelectedIds}
                        title={t('tabs.createSurvey.step4Content.availableQuestionsTitle')}
                    />
                </StepConfigurePanelStyled>

                <StepConfigureActionsStyled>
                    <Button
                        disabled={pendingSelectedIds.length === 0}
                        onClick={handleAddSelected}
                        sx={{
                            backgroundColor: colors.default,
                            color: colors.grey000,
                            minWidth: 0,
                            padding: '8px',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: colors.defaultHover },
                            '&.Mui-disabled': { backgroundColor: colors.grey500, color: colors.grey400 },
                        }}
                        variant="contained"
                    >
                        <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </Button>
                    <TypographyStyledGlobal
                        color={colors.grey400}
                        size={fontSize.s}
                        style={{ textAlign: 'center', maxWidth: '60px' }}
                    >
                        {t('tabs.createSurvey.step3Content.addSelectedHint', { count: pendingSelectedIds.length })}
                    </TypographyStyledGlobal>
                </StepConfigureActionsStyled>

                <StepConfigurePanelStyled>
                    <SelectedQuestionsPanel
                        onConfigure={handleConfigure}
                        onRemove={handleRemove}
                        onRename={handleRename}
                        onReorder={onSelectedFiltersChange}
                        selectedQuestions={selectedFilters}
                        title={t('tabs.createSurvey.step4Content.selectedFiltersTitle')}
                        filterMode
                    />
                </StepConfigurePanelStyled>
            </StepConfigureContainerStyled>
            <StepConfigureFooterStyled>
                <SimpleActionButton handleClickConfirm={() => handleNextStep('#step4')} labelConfirm={t('form.nextStep')} />
            </StepConfigureFooterStyled>
        </>
    );
};

export default StepSelectFilters;
